import React from 'react'
import { useHistory } from 'react-router-dom'
import { withStyles } from '@material-ui/core/styles'
import {
  Typography,
  Button,
  Box,
  CircularProgress,
  Tooltip,
} from '@material-ui/core'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import EditIcon from '@material-ui/icons/Edit'
import ScheduleIcon from '@material-ui/icons/Schedule'
import FolderIcon from '@material-ui/icons/FolderOpen'
import PlayArrowIcon from '@material-ui/icons/PlayArrow'
import DeleteIcon from '@material-ui/icons/DeleteOutline'
import URL from 'src/api/url/api'
import styles from './styles'

const slugify = (text) => {
  return (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
}

const STEP_NAMES = ['Connection', 'Planner', 'Analysis', 'Generation']

const STATUS_CONFIG = {
  in_progress: { label: 'Active', bg: '#F0F9FF', color: '#0369A1' },
  completed: { label: 'Success', bg: '#F0FDF4', color: '#15803D' },
  failed: { label: 'Failed', bg: '#FEF2F2', color: '#B91C1C' },
  cancelled: { label: 'Cancelled', bg: '#F8FAFC', color: '#64748B' },
}

const SessionActions = ({ classes, session, onResume, onCancel, history }) => {
  const isCompleted = session.status === 'completed'
  const result = session.generate_result

  if (isCompleted && result) {
    const isCollection = result.collectionId && result.courses && result.courses.length > 0
    let editUrl = ''
    let shareUrl = ''

    if (isCollection) {
      const slug = slugify(result.collectionName || 'collection')
      shareUrl = `${URL.APP_URL}category/${slug}-1/${result.collectionId}`
      editUrl = `/collection/${result.collectionId}/mini-courses`
    } else {
      shareUrl = result.publicUrl
      editUrl = `/mini-course/${result.courseId}`
    }

    return (
      <div className={classes.sessionActions}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<EditIcon style={{ fontSize: '14px' }} />}
          onClick={() => history.push(editUrl)}
          className={`${classes.actionButton} ${classes.secondaryBtn}`}
        >
          Edit
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<OpenInNewIcon style={{ fontSize: '14px' }} />}
          href={shareUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`${classes.actionButton} ${classes.secondaryBtn}`}
        >
          Share
        </Button>
      </div>
    )
  }

  if (session.status === 'in_progress') {
    return (
      <div className={classes.sessionActions}>
        <Tooltip title="Delete Session">
          <Button
            size="small"
            onClick={() => onCancel(session.session_id)}
            className={`${classes.actionButton} ${classes.secondaryBtn}`}
            style={{ minWidth: 'auto', padding: '6px 8px' }}
          >
            <DeleteIcon fontSize="small" />
          </Button>
        </Tooltip>
        <Button
          size="small"
          variant="contained"
          startIcon={<PlayArrowIcon style={{ fontSize: '14px' }} />}
          onClick={() => onResume(session.session_id)}
          className={`${classes.actionButton} ${classes.resumeBtn}`}
        >
          Resume
        </Button>
      </div>
    )
  }

  return null
}

const TaskManager = ({ classes, sessions, loading, onResume, onCancel }) => {
  const history = useHistory()

  if (loading && (!sessions || sessions.length === 0)) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" p={4} gridGap="12px">
        <CircularProgress size={20} thickness={5} />
        <Typography style={{ color: '#64748B', fontSize: '14px' }}>Updating sessions list...</Typography>
      </Box>
    )
  }

  if (!sessions || sessions.length === 0) {
    return (
      <Box textAlign="center" py={6} style={{ border: '2px dashed #E2E8F0', borderRadius: '16px' }}>
        <Typography style={{ color: '#64748B', fontSize: '15px' }}>
          No active sessions found. Start by clicking "New Session".
        </Typography>
      </Box>
    )
  }

  return (
    <div className={classes.root}>
      {sessions.map(s => {
        const config = STATUS_CONFIG[s.status] || STATUS_CONFIG.cancelled
        const stepName = STEP_NAMES[s.current_step] || 'Unknown'
        const createdAt = s.created_at
          ? new Date(s.created_at).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })
          : ''

        return (
          <div key={s.session_id} className={classes.sessionCard}>
            <div className={classes.sessionInfo}>
              <Box display="flex" alignItems="center" gridGap="10px">
                <Typography className={classes.sessionLabel}>
                  {s.label || s.folder_name || 'Untitled Project'}
                </Typography>
                <div
                  className={classes.statusBadge}
                  style={{ backgroundColor: config.bg, color: config.color }}
                >
                  {config.label}
                </div>
              </Box>
              <div className={classes.sessionMeta}>
                <div className={classes.metaText}>
                  <FolderIcon style={{ fontSize: '14px' }} />
                  {s.folder_name || 'No folder selected'}
                </div>
                <div className={classes.metaText}>
                  <ScheduleIcon style={{ fontSize: '14px' }} />
                  {createdAt}
                </div>
                {s.status === 'in_progress' && (
                  <div className={classes.metaText} style={{ color: '#0369A1', fontWeight: 600 }}>
                    Current Step: {stepName}
                  </div>
                )}
              </div>
            </div>
            <SessionActions
              classes={classes}
              session={s}
              onResume={onResume}
              onCancel={onCancel}
              history={history}
            />
          </div>
        )
      })}
    </div>
  )
}

export default withStyles(styles)(TaskManager)
